import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Users, Calendar, AlertTriangle, Clock, Shield, BarChart3, Download, Award, CheckCircle, Smartphone, Printer, Share2, Copy, Send, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { toast } from "sonner";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
export default function DocumentationPage() {
  const navigate = useNavigate();
  
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [isEmailSending, setIsEmailSending] = useState(false);
  const systemFeatures = [{
    title: "Guard Management System",
    description: "Complete security personnel management including profiles, certifications, and assignments",
    icon: Shield,
    features: ["Guard profile creation and management", "Certification tracking and renewal alerts", "Assignment and shift management", "Performance tracking and evaluation", "Training record management"]
  }, {
    title: "Client Relationship Management",
    description: "Comprehensive client management and service delivery tracking",
    icon: Users,
    features: ["Client profile and contract management", "Service agreement tracking", "Communication logs and history", "Billing and invoicing integration", "Service quality monitoring"]
  }, {
    title: "Mission Creation & Management",
    description: "Advanced mission planning and execution system for security operations",
    icon: FileText,
    features: ["Dynamic mission creation and assignment", "Task breakdown and priority setting", "Resource allocation and planning", "Mission progress tracking", "Objective completion verification", "Post-mission reporting and analysis"]
  }, {
    title: "Security Teams Management",
    description: "Comprehensive team organization and coordination platform",
    icon: Users,
    features: ["Team structure and hierarchy management", "Role-based access control", "Team member assignment and rotation", "Skill-based team composition", "Cross-team collaboration tools", "Team performance analytics"]
  }, {
    title: "Enhanced Communication System",
    description: "End-to-end encrypted communication with targeted notifications",
    icon: Users,
    features: ["Real-time internal chat system", "End-to-end encryption for secure messaging", "Team-based communication channels", "Targeted email notification system", "File sharing and document collaboration", "Message history and search capabilities", "Emergency broadcast messaging"]
  }, {
    title: "Complete Patrol Management",
    description: "Advanced patrol monitoring and management system with GPS tracking",
    icon: Shield,
    features: ["Real-time GPS tracking and monitoring", "Patrol route planning and optimization", "Checkpoint verification system", "Incident reporting during patrols", "Patrol schedule automation", "Route deviation alerts", "Patrol performance analytics"]
  }, {
    title: "Scheduling & Workforce Planning",
    description: "Advanced scheduling system for optimal resource allocation",
    icon: Calendar,
    features: ["Automated shift scheduling", "Guard availability management", "Overtime tracking and alerts", "Holiday and leave integration", "Real-time schedule adjustments"]
  }, {
    title: "Incident Management",
    description: "Complete incident reporting, tracking, and resolution system",
    icon: AlertTriangle,
    features: ["Real-time incident reporting", "Incident categorization and prioritization", "Investigation workflow management", "Document and evidence management", "Resolution tracking and follow-up"]
  }, {
    title: "Leave Management",
    description: "Comprehensive leave request and approval workflow",
    icon: Clock,
    features: ["Digital leave request submission", "Multi-level approval workflows", "Leave balance tracking", "Calendar integration", "Automated notifications"]
  }, {
    title: "HR Recruitment Process",
    description: "End-to-end candidate management and hiring workflow",
    icon: FileText,
    features: ["Application tracking system", "Interview scheduling and management", "Skills and psychological testing", "Background check integration", "Onboarding workflow automation"]
  }];
  const processes = [{
    title: "Mission Creation & Assignment Workflow",
    description: "Comprehensive mission planning and execution process",
    steps: ["Mission objective definition and planning", "Resource allocation and team assignment", "Route planning and checkpoint setup", "Task breakdown and priority setting", "Real-time mission monitoring", "Completion verification and reporting"]
  }, {
    title: "Security Team Management Process",
    description: "Complete team organization and coordination workflow",
    steps: ["Team structure design and hierarchy setup", "Role-based access control configuration", "Member assignment and skill matching", "Team communication channel setup", "Performance monitoring and evaluation", "Cross-team collaboration coordination"]
  }, {
    title: "Enhanced Communication Workflow",
    description: "End-to-end encrypted communication system processes",
    steps: ["Secure chat channel initialization", "End-to-end encryption key exchange", "Targeted notification setup and delivery", "File sharing with security verification", "Message archiving and compliance", "Emergency broadcast procedures"]
  }, {
    title: "Patrol Management Process",
    description: "Complete patrol planning, execution, and monitoring workflow",
    steps: ["Patrol route optimization and planning", "GPS tracking and geofence setup", "Checkpoint verification configuration", "Real-time monitoring and alerts", "Incident response during patrols", "Post-patrol analysis and reporting"]
  }, {
    title: "Hiring Process Workflow",
    description: "Complete recruitment and onboarding procedures",
    steps: ["Application submission and initial screening", "First interview scheduling and evaluation", "Second interview and assessment", "Cognitive and psychological testing", "Background verification", "Final decision and onboarding"]
  }, {
    title: "Incident Response Procedures",
    description: "Standard operating procedures for incident handling",
    steps: ["Immediate incident detection and reporting", "Initial response and containment", "Investigation and evidence collection", "Stakeholder notification and communication", "Resolution implementation", "Post-incident review and documentation"]
  }, {
    title: "Scheduling Management Process",
    description: "Workflow for effective workforce scheduling",
    steps: ["Demand forecasting and analysis", "Guard availability collection", "Schedule generation and optimization", "Conflict resolution and adjustments", "Schedule distribution and confirmation", "Real-time monitoring and updates"]
  }];
  const exportToPDF = async () => {
    toast.success("Generating PDF...");

    // Simple, working approach - create a printable version
    const content = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>OVIT Shiftask Documentation</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 20px; color: #333; }
    .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
    .title { color: #1e40af; font-size: 32px; font-weight: bold; margin: 0; }
    .subtitle { color: #64748b; font-size: 14px; margin: 5px 0; }
    .app-title { color: #2563eb; font-size: 24px; font-weight: 600; margin: 15px 0 5px 0; }
    .section { margin-bottom: 30px; }
    .section-title { color: #1e40af; font-size: 18px; font-weight: 600; border-left: 4px solid #2563eb; padding-left: 12px; margin-bottom: 15px; }
    .feature { background: #f8fafc; padding: 15px; margin-bottom: 12px; border-radius: 6px; border: 1px solid #e2e8f0; }
    .feature-title { color: #1f2937; font-size: 16px; font-weight: 600; margin: 0 0 8px 0; }
    .feature-desc { color: #6b7280; font-size: 14px; margin: 0 0 10px 0; }
    .feature-list { margin: 0; padding-left: 18px; }
    .feature-list li { color: #374151; font-size: 13px; margin-bottom: 4px; }
    .cert-grid { display: flex; flex-wrap: wrap; gap: 10px; justify-content: space-around; margin-bottom: 20px; }
    .cert { background: #f8fafc; padding: 12px; border-radius: 6px; border: 1px solid #e2e8f0; text-align: center; flex: 1; min-width: 100px; }
    .cert-name { font-weight: 600; color: #1e40af; font-size: 14px; }
    .cert-desc { font-size: 12px; color: #64748b; }
    .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
  </style>
</head>
<body>
  <div class="header">
    <h1 class="title">OVIT</h1>
    <p class="subtitle">Security Solutions</p>
    <h2 class="app-title">Shiftask</h2>
    <p class="subtitle">Security Management Application - Documentation</p>
    <p style="color: #475569;">Comprehensive Security Management System - ISO Certified & Audit Ready</p>
  </div>

  <div class="section">
    <h3 class="section-title">Certifications & Compliance</h3>
    <div class="cert-grid">
      <div class="cert"><div class="cert-name">ISO 27001</div><div class="cert-desc">Information Security</div></div>
      <div class="cert"><div class="cert-name">ISO 18788</div><div class="cert-desc">Private Security Operations</div></div>
      <div class="cert"><div class="cert-name">ISO 45001</div><div class="cert-desc">Occupational Health & Safety</div></div>
      <div class="cert"><div class="cert-name">ISO 9001</div><div class="cert-desc">Quality Management</div></div>
      <div class="cert" style="background: #f0fdf4; border-color: #bbf7d0;"><div class="cert-name" style="color: #15803d;">GDPR Compliant</div><div class="cert-desc" style="color: #65a30d;">Data Protection</div></div>
    </div>
  </div>

  <div class="section">
    <h3 class="section-title">System Features</h3>
    ${systemFeatures.map(feature => `
      <div class="feature">
        <h4 class="feature-title">${feature.title}</h4>
        <p class="feature-desc">${feature.description}</p>
        <ul class="feature-list">
          ${feature.features.map(f => `<li>${f}</li>`).join('')}
        </ul>
      </div>
    `).join('')}
  </div>

  <div class="section">
    <h3 class="section-title">Key Processes</h3>
    ${processes.map(process => `
      <div class="feature">
        <h4 class="feature-title">${process.title}</h4>
        <p class="feature-desc">${process.description}</p>
        <ol class="feature-list">
          ${process.steps.map(step => `<li>${step}</li>`).join('')}
        </ol>
      </div>
    `).join('')}
  </div>

  <div class="section">
    <h3 class="section-title">Emergency Evacuation Management System</h3>
    <div class="feature">
      <h4 class="feature-title">Advanced Emergency Response Technology</h4>
      <p class="feature-desc">Comprehensive emergency evacuation management system ensuring organizational safety and regulatory compliance through automated response protocols.</p>
      
      <div style="margin: 20px 0;">
        <h5 style="color: #1e40af; font-weight: 600; margin-bottom: 10px;">Core Features</h5>
        <ul class="feature-list">
          <li>Multi-channel notification system with delivery confirmation</li>
          <li>QR code integration for quick identification and location tracking</li>
          <li>Comprehensive reporting with compliance documentation</li>
          <li>Pre-configured emergency templates for rapid response</li>
          <li>Real-time personnel accountability tracking</li>
          <li>Mobile-compatible emergency response interface</li>
        </ul>
      </div>

      <div style="margin: 20px 0;">
        <h5 style="color: #1e40af; font-weight: 600; margin-bottom: 10px;">Compliance Benefits</h5>
        <ul class="feature-list">
          <li>OSHA emergency action plan compliance</li>
          <li>Fire safety regulation adherence</li>
          <li>Workplace safety standards implementation</li>
          <li>Risk mitigation through documented procedures</li>
          <li>Complete personnel accountability assurance</li>
          <li>Evidence of due diligence provision</li>
        </ul>
      </div>

      <div style="margin: 20px 0;">
        <h5 style="color: #1e40af; font-weight: 600; margin-bottom: 10px;">Technology Integration</h5>
        <ul class="feature-list">
          <li>Instant alerts with multi-channel delivery</li>
          <li>QR code scanning for location verification</li>
          <li>Analytics and reporting dashboard</li>
          <li>Rapid response coordination tools</li>
          <li>Automated emergency protocol activation</li>
          <li>Real-time communication during emergencies</li>
        </ul>
      </div>
    </div>
  </div>

  <div class="footer">
    <p>Generated from OVIT Shiftask Documentation System</p>
    <p>Document generated on ${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}</p>
  </div>
</body>
</html>`;

    // Create blob and download directly
    const blob = new Blob([content], {
      type: 'text/html'
    });
    const url = URL.createObjectURL(blob);

    // Use html2pdf on the blob content
    const opt = {
      margin: 1,
      filename: 'OVIT-Shiftask-Documentation.pdf',
      image: {
        type: 'jpeg',
        quality: 0.98
      },
      html2canvas: {
        scale: 2,
        useCORS: true
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'portrait'
      }
    };
    const { default: html2pdf } = await import("html2pdf.js");
    html2pdf().from(content).set(opt).save().then(() => {
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully!");
    }).catch(error => {
      console.error('PDF Error:', error);
      toast.error("Failed to generate PDF");
    });
  };
  const generateShareableLink = () => {
    const publicUrl = window.location.origin + '/public-documentation';
    navigator.clipboard.writeText(publicUrl).then(() => {
      toast.success("Public documentation link copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };

  const sendHomepageEmail = async () => {
    if (!recipientEmail) {
      toast.error("Please enter recipient email");
      return;
    }

    setIsEmailSending(true);

    try {
      // Fetch the homepage content
      const response = await fetch(window.location.origin);
      let htmlContent = await response.text();

      // Clean up the HTML - remove unnecessary buttons and elements
      htmlContent = htmlContent
        .replace(/<button[^>]*>.*?<\/button>/gi, '') // Remove all buttons
        .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove scripts
        .replace(/class="[^"]*"/gi, '') // Remove classes to avoid styling conflicts
        .replace(/<style[^>]*>.*?<\/style>/gi, ''); // Remove style tags

      // Send email using Supabase edge function
      console.log('Attempting to call edge function...');
      const { data, error } = await supabase.functions.invoke('send-homepage-email', {
        body: {
          recipientEmail,
          recipientName,
          htmlContent
        }
      });
      
      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Email error:', error);
        toast.error("Failed to send email: " + error.message);
      } else {
        toast.success("Homepage sent successfully!");
        setEmailDialogOpen(false);
        setRecipientEmail("");
        setRecipientName("");
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Failed to send email");
    } finally {
      setIsEmailSending(false);
    }
  };
  return <div id="documentation-content" className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Enhanced Header */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 text-white">
        <div className="container mx-auto px-6 py-12">
          
          
          {/* Company Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <img
                src="/images/ovit-logo-64.webp"
                srcSet="/images/ovit-logo-16.webp 16w, /images/ovit-logo-24.webp 24w, /images/ovit-logo-48.webp 48w, /images/ovit-logo-64.webp 64w, /images/ovit-logo-80.webp 80w"
                sizes="64px"
                alt="OVIT Logo"
                width="64"
                height="64"
                decoding="async"
                className="h-16 w-16 rounded-full"
              />
              <div className="text-left">
                <h1 className="text-6xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                  OVIT
                </h1>
                <p className="text-blue-200 text-sm font-medium tracking-wider">Security Solutions</p>
              </div>
            </div>
            
            <div className="mb-6">
              <h2 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-100 to-white bg-clip-text text-transparent">
                Shiftask
              </h2>
              <p className="text-blue-200 text-lg">Security Management Application</p>
            </div>
            
            
            <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
              Comprehensive Security Management System - ISO Certified & Audit Ready
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-3 mb-8">
            <Button onClick={generateShareableLink} variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-xs px-3 py-1.5">
              <Copy className="h-3 w-3 mr-1.5" />
              Copy Link
            </Button>
            
            <Button onClick={exportToPDF} variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-xs px-3 py-1.5">
              <Download className="h-3 w-3 mr-1.5" />
              Export PDF
            </Button>

            <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="bg-white/10 border-white/20 text-white hover:bg-white/20 text-xs px-3 py-1.5">
                  <Send className="h-3 w-3 mr-1.5" />
                  Email Homepage
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Send Homepage via Email</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="recipientName">Recipient Name</Label>
                    <Input
                      id="recipientName"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      placeholder="Enter recipient's name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recipientEmail">Recipient Email</Label>
                    <Input
                      id="recipientEmail"
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Enter recipient's email"
                    />
                  </div>
                  <Button 
                    onClick={sendHomepageEmail} 
                    disabled={isEmailSending}
                    className="w-full"
                  >
                    {isEmailSending ? (
                      <>
                        <Mail className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Certification Badges */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <button onClick={() => {
            const img = new Image();
            img.src = "/lovable-uploads/b7b22af3-5f53-41da-8e9a-0668c13a8d07.png";
            const w = window.open("");
            if (w) {
              w.document.write(`<img src="${img.src}" style="width:100%;height:100%;object-fit:contain;" alt="ISO 27001 Certificate"/>`);
            }
          }} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20 hover:bg-white/20 transition-all cursor-pointer">
              <Award className="h-6 w-6 text-yellow-300" />
              <div className="text-left">
                <div className="font-semibold text-sm">ISO 27001</div>
                <div className="text-xs text-blue-200">Information Security</div>
              </div>
            </button>
            
            <button onClick={() => {
            const img = new Image();
            img.src = "/lovable-uploads/000e606a-6c60-43e1-80d5-7ebf153088e3.png";
            const w = window.open("");
            if (w) {
              w.document.write(`<img src="${img.src}" style="width:100%;height:100%;object-fit:contain;" alt="ISO 18788 Certificate"/>`);
            }
          }} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20 hover:bg-white/20 transition-all cursor-pointer">
              <Award className="h-6 w-6 text-yellow-300" />
              <div className="text-left">
                <div className="font-semibold text-sm">ISO 18788</div>
                <div className="text-xs text-blue-200">Private Security Operations</div>
              </div>
            </button>
            
            <button onClick={() => {
            const img = new Image();
            img.src = "/lovable-uploads/366cb551-3a8c-48eb-a48b-2023edc97220.png";
            const w = window.open("");
            if (w) {
              w.document.write(`<img src="${img.src}" style="width:100%;height:100%;object-fit:contain;" alt="ISO 45001 Certificate"/>`);
            }
          }} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20 hover:bg-white/20 transition-all cursor-pointer">
              <Award className="h-6 w-6 text-yellow-300" />
              <div className="text-left">
                <div className="font-semibold text-sm">ISO 45001</div>
                <div className="text-xs text-blue-200">Occupational Health & Safety</div>
              </div>
            </button>
            
            <button onClick={() => {
            const img = new Image();
            img.src = "/lovable-uploads/5890ea62-ec35-4e48-b28d-7e174c1afc39.png";
            const w = window.open("");
            if (w) {
              w.document.write(`<img src="${img.src}" style="width:100%;height:100%;object-fit:contain;" alt="ISO 9001 Certificate"/>`);
            }
          }} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20 hover:bg-white/20 transition-all cursor-pointer">
              <Award className="h-6 w-6 text-yellow-300" />
              <div className="text-left">
                <div className="font-semibold text-sm">ISO 9001</div>
                <div className="text-xs text-blue-200">Quality Management</div>
              </div>
            </button>
            
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <CheckCircle className="h-6 w-6 text-green-300" />
              <div className="text-left">
                <div className="font-semibold text-sm">GDPR Compliant</div>
                <div className="text-xs text-blue-200">Data Protection</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <img
                src="/images/ovit-logo-24.webp"
                srcSet="/images/ovit-logo-16.webp 16w, /images/ovit-logo-24.webp 24w, /images/ovit-logo-48.webp 48w"
                sizes="24px"
                alt="OVIT Logo"
                width="24"
                height="24"
                loading="lazy"
                decoding="async"
                className="h-6 w-6 rounded-full"
              />
              <div className="text-left">
                <div className="font-semibold text-sm">Audit Ready</div>
                <div className="text-xs text-blue-200">Complete Documentation</div>
              </div>
            </div>
          </div>

          {/* Mobile App Store Badges */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a href="https://apps.apple.com/gr/app/shiftask/id6745031000" target="_blank" rel="noopener noreferrer" className="group transition-transform hover:scale-105">
              <div className="bg-black rounded-lg px-6 py-3 flex items-center gap-3 border border-white/20 hover:bg-gray-900 transition-colors">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <div className="text-xs text-gray-300">Download on the</div>
                  <div className="text-lg font-semibold text-white">App Store</div>
                </div>
              </div>
            </a>
            
            <a href="https://play.google.com/store/apps/details?id=com.drivaskostas1980.shiftask&hl=en" target="_blank" rel="noopener noreferrer" className="group transition-transform hover:scale-105">
              <div className="bg-black rounded-lg px-6 py-3 flex items-center gap-3 border border-white/20 hover:bg-gray-900 transition-colors">
                <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                </svg>
                <div className="text-left">
                  <div className="text-xs text-gray-300">Get it on</div>
                  <div className="text-lg font-semibold text-white">Google Play</div>
                </div>
              </div>
            </a>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-3xl font-bold text-white">10</div>
              <div className="text-sm text-blue-200">Core Modules</div>
            </div>
            <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-3xl font-bold text-white">25+</div>
              <div className="text-sm text-blue-200">Automated Workflows</div>
            </div>
            <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-3xl font-bold text-white">24/7</div>
              <div className="text-sm text-blue-200">System Availability</div>
            </div>
            <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-sm text-blue-200">Compliance Ready</div>
            </div>
            <div className="text-center p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
              <Smartphone className="h-8 w-8 text-white mx-auto mb-2" />
              <div className="text-sm text-blue-200">Cross-Platform</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto p-6 space-y-8 relative -mt-8">
        {/* System Overview Card */}
        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <BarChart3 className="h-6 w-6" />
              System Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-lg leading-relaxed">
              OVIT's Security Management System is a comprehensive, ISO-certified platform designed to streamline 
              security operations, enhance workforce management, and ensure compliance with international standards. 
              The system integrates multiple modules to provide end-to-end solutions for security service providers.
            </p>
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-3 text-blue-900">Compliance & Certifications</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Award className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium">ISO 27001:2013</div>
                    <div className="text-sm text-muted-foreground">Information Security Management</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium">ISO 18788:2015</div>
                    <div className="text-sm text-muted-foreground">Private Security Operations Management</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium">ISO 45001:2018</div>
                    <div className="text-sm text-muted-foreground">Occupational Health & Safety Management</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Award className="h-5 w-5 text-yellow-600" />
                  <div>
                    <div className="font-medium">ISO 9001:2015</div>
                    <div className="text-sm text-muted-foreground">Quality Management Systems</div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Core Features */}
        <div>
        <h2 className="text-3xl font-bold mb-6">Core System Features</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {systemFeatures.map((feature, index) => <Card key={index} className="h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <feature.icon className="h-5 w-5 text-primary" />
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{feature.description}</p>
                <div className="space-y-2">
                  <div className="font-semibold text-sm">Key Features:</div>
                  <ul className="space-y-1">
                    {feature.features.map((item, idx) => <li key={idx} className="text-sm flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2 flex-shrink-0" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>

      {/* Process Documentation */}
      <div>
        <h2 className="text-3xl font-bold mb-6">Process Documentation</h2>
        <div className="space-y-6">
          {processes.map((process, index) => <Card key={index}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {process.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">{process.description}</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {process.steps.map((step, idx) => <div key={idx} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <div className="text-sm">{step}</div>
                    </div>)}
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>

      {/* Technical Specifications */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Technical Specifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Technology Stack</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Frontend: React 18 with TypeScript</li>
                <li>• Backend: Supabase with PostgreSQL</li>
                <li>• Authentication: Supabase Auth</li>
                <li>• UI Framework: Tailwind CSS + shadcn/ui</li>
                <li>• Real-time: WebSocket connections</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Security Features</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• Role-based access control (RBAC)</li>
                <li>• Row-level security (RLS) policies</li>
                <li>• End-to-end encryption</li>
                <li>• Audit logging and compliance</li>
                <li>• Multi-factor authentication support</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compliance & Standards */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Compliance & Standards</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-4">
            Our system adheres to industry standards and regulatory requirements for security service providers.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 border rounded-lg text-center">
              <div className="font-semibold">Data Protection</div>
              <div className="text-sm text-muted-foreground">GDPR Compliant</div>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <div className="font-semibold">Security Standards</div>
              <div className="text-sm text-muted-foreground">ISO 27001 Aligned</div>
            </div>
            <div className="p-4 border rounded-lg text-center">
              <div className="font-semibold">Audit Ready</div>
              <div className="text-sm text-muted-foreground">Complete Audit Trails</div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>;
}